import type { RatingKey } from '@risksmart-app/components/src/hooks/types';
import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import type { FormId } from '@risksmart-app/shared/forms/formConfigRegistry';
import { getFormConfigRegistry } from '@risksmart-app/shared/forms/formConfigRegistry';
import type { FormConfig } from '@risksmart-app/shared/forms/types';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';
import { useModulesStore } from 'src/context/moduleContext';

import { useRiskSmartForm } from '@/components/form/form/customisable-form/RiskSmartFormContext';
import { useCustomisableFormDataContext } from '@/components/form/form/customisable-form-data/CustomisableFormDataContext';
import {
  useGetDepartments,
  useGetTags,
  useGetUserGroups,
  useGetUsers,
} from '@/hooks/queries';
import type { CommonKeys } from '@/hooks/useCommonLookupLazy';
import { useCommonLookupLazy } from '@/hooks/useCommonLookupLazy';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { getCustomAttributeRenderProps } from '@/utils/table/utils/customAttributes';

import type { DescriptorOptionSources } from './buildActiveFormDescriptor';
import { buildActiveFormDescriptor } from './buildActiveFormDescriptor';
import { createApplyPrefill } from './createApplyPrefill';
import { resolveCustomAttributeOptionFields } from './resolveCustomAttributeOptionFields';
import { resolveIdentityCandidates } from './resolveIdentityCandidates';
import type { ApplyPrefillField, DescriptorFieldOption } from './types';
import { useActiveFormStore } from './useActiveFormStore';

/**
 * Publishes the active form's prefillable state to the `useActiveFormStore`
 * bridge so the AI chat panel (mounted outside this provider) can read the
 * form's fields and write proposed values into it. Rendered ONLY when a form
 * opts in via `publishActiveForm` (gate that at the call site on the feature
 * flag), and only for forms with a known `parentType`. Single-slot: it clears
 * the slot on unmount, mirroring `ToolbarStatePublisher`.
 *
 * `recordId` is supplied by the opt-in call site (which already resolves it via
 * `useGetGuidParam`), NOT derived from the route here: the generic publisher
 * can't reliably pick the form's own id off the route — param names vary
 * (`actionId`, `controlGroupId`, plain `id`) and a nested route like
 * `/risks/:riskId/controls/:controlId` exposes several ids at once.
 */
export const ActiveFormPublisher = <TFieldValues extends FieldValues>({
  methods,
  parentType,
  recordId,
}: {
  methods: UseFormReturn<TFieldValues>;
  parentType: FormId;
  recordId: string | null;
}) => {
  const { allFieldIds, defaultRequiredFields, forcedRequiredFields } =
    useRiskSmartForm();
  const modules = useModulesStore((s) => s.modules);
  const formSectionsEnabled = useIsFeatureFlagEnabled('dynamic_forms');
  const { customAttributeSchema } = useCustomisableFormDataContext();

  // Both resolvers are LAZY by key, so one hook call serves every closed-option
  // field on the form. `useRating` is called with no key for that reason — the
  // per-key work happens in `getOptionsByRatingKey`.
  const { getOptionsByRatingKey } = useRating();
  const { getOptions: getCommonLookupOptions } = useCommonLookupLazy();

  /**
   * The identity vocabularies a proposed NAME resolves against (AID-394), read
   * from the same four queries the form's own pickers use — so the candidate set
   * is exactly what the user could have picked by hand.
   *
   * Unconditional, because hooks are. That is cheaper than it looks: all four
   * are org reference data on a shared stale time and `useGetUsers` is
   * `cache-first` specifically so its ~8 concurrent consumers dedupe into one
   * request, so on any form that HAS an owner or tag picker these resolve
   * against a cache the picker was going to populate anyway. A form with no
   * identity fields pays a cached read whose result the descriptor then ignores.
   */
  const currentUser = useRisksmartUser();
  const { data: users } = useGetUsers({ queryArgs: {} });
  const { data: userGroups } = useGetUserGroups({ queryArgs: {} });
  const { data: tags } = useGetTags({ queryArgs: {} });
  const { data: departments } = useGetDepartments({ queryArgs: {} });

  // Memoised on the DIRECTORY ALONE, deliberately not folded into
  // `optionSources` below. This mapping is O(users) — thousands of rows on a
  // large org — while the taxonomy resolvers it would otherwise share a memo
  // with are `useCallback`s whose identity ultimately depends on i18n `t`. Kept
  // separate, a resource reload re-creates the (lazy, cheap) option closures
  // without re-walking the whole directory.
  const identityCandidates = useMemo(
    () =>
      resolveIdentityCandidates({
        users: users?.user ?? [],
        userGroups: userGroups?.user_group ?? [],
        tagTypes: tags?.tag_type ?? [],
        departmentTypes: departments?.department_type ?? [],
        isCustomerSupport: !!currentUser.user?.isCustomerSupport,
      }),
    [
      users?.user,
      userGroups?.user_group,
      tags?.tag_type,
      departments?.department_type,
      currentUser.user?.isCustomerSupport,
    ]
  );

  const setFormState = useActiveFormStore((s) => s.setFormState);
  const clearFormState = useActiveFormStore((s) => s.clearFormState);

  /**
   * The closed option sets for this form, read from the SAME sources the form's
   * own controls read (AID-393):
   *
   *  - `rating` → the org's rating taxonomy (i18n `ratings`, with the org's
   *    `TaxonomyOverrides.Rating` merged in), which carries each value in its
   *    native type — the discrimination AID-240 §6 said the form config could
   *    not provide.
   *  - `commonLookup` → the `common` i18n namespace.
   *  - custom-attribute `select` / `multiselect` → the org's custom-attribute
   *    schema.
   *  - `users` / `tags` / `departments` → the org's directory and vocabularies,
   *    as NAME→UUID candidates rather than published options (AID-394). These
   *    are the only entry here that never reaches the model.
   *
   * Reading the control's own source is what makes this set authoritative: it is
   * exactly what the user could pick by hand on this form, for this org.
   */
  const optionSources = useMemo<DescriptorOptionSources>(
    () => ({
      // The two casts below are the adapter boundary: `DescriptorOptionSources`
      // takes plain strings so `buildActiveFormDescriptor` stays a pure module
      // with no dependency on i18n key typings, while these resolvers want the
      // narrow `KeyPrefix` types. The keys themselves come from the form config,
      // where they are already declared as those types.
      getRatingOptions: (ratingKey: string): DescriptorFieldOption[] =>
        getOptionsByRatingKey(ratingKey as RatingKey)
          // A taxonomy entry may carry `value: null` (the "unrated" band, which
          // exists to colour a range rather than to be selected). Prefill never
          // proposes "no value", so those are dropped rather than published as
          // a legal option.
          .filter(
            (option): option is { label: string; value: number | string } =>
              option.value !== null && option.value !== undefined
          )
          .map(({ value, label }) => ({ value, label })),
      getCommonLookupOptions: (i18nKey: string) =>
        getCommonLookupOptions(i18nKey as CommonKeys),
      customAttributeOptionFields: customAttributeSchema
        ? resolveCustomAttributeOptionFields(
            getCustomAttributeRenderProps(
              customAttributeSchema.Schema,
              customAttributeSchema.UiSchema
            )
          )
        : new Map(),
      identityCandidates,
    }),
    [
      getOptionsByRatingKey,
      getCommonLookupOptions,
      customAttributeSchema,
      identityCandidates,
    ]
  );

  const descriptor = useMemo(() => {
    const formConfig = getFormConfigRegistry(formSectionsEnabled, modules)[
      parentType
    ] as FormConfig;
    const requiredFieldIds = new Set([
      ...defaultRequiredFields,
      ...forcedRequiredFields,
    ]);

    return buildActiveFormDescriptor(
      formConfig,
      allFieldIds,
      requiredFieldIds,
      optionSources
    );
  }, [
    parentType,
    formSectionsEnabled,
    modules,
    allFieldIds,
    defaultRequiredFields,
    forcedRequiredFields,
    optionSources,
  ]);

  // Read the latest methods / descriptor through refs so the published
  // `applyPrefill` / `getValues` stay referentially stable and the publish
  // effect below depends only on the DATA (descriptor / record / entity), not on
  // the per-render identity of `methods` — mirrors ToolbarStatePublisher (CUS-134).
  const methodsRef = useRef(methods);
  methodsRef.current = methods;
  const descriptorRef = useRef(descriptor);
  descriptorRef.current = descriptor;

  const getValues = useCallback(
    (): Record<string, unknown> => methodsRef.current.getValues(),
    []
  );

  const applyPrefill = useCallback((fields: ApplyPrefillField[]) => {
    const setFieldValue = (name: string, value: unknown): void => {
      // RHF's setValue is keyed by FieldPath<TFieldValues>; prefill targets are
      // dynamic canonical names already validated against the descriptor, so the
      // generic-string setter is the correct shape at this boundary.
      (
        methodsRef.current.setValue as (
          n: string,
          v: unknown,
          o: { shouldDirty: boolean; shouldValidate: boolean }
        ) => void
      )(name, value, { shouldDirty: true, shouldValidate: true });
    };

    return createApplyPrefill(descriptorRef.current, setFieldValue)(fields);
  }, []);

  // Publish the latest state on change. Deliberately NO cleanup here: an effect
  // cleanup runs before every re-run, so clearing here would blip the slot to
  // `null` on every descriptor/record change — a reactive consumer (the chat
  // store) could observe that null and discard its proposal. Clearing is an
  // unmount-only concern, handled by the separate effect below.
  useEffect(() => {
    setFormState({
      entityType: parentType,
      recordId,
      descriptor,
      applyPrefill,
      getValues,
    });
  }, [parentType, recordId, descriptor, applyPrefill, getValues, setFormState]);

  // Clear the slot only when the publisher unmounts (tab switch / navigation),
  // never on a mid-life update.
  useEffect(() => () => clearFormState(), [clearFormState]);

  return null;
};
